import React, {useEffect, useMemo, useState} from "react";
import {collection, doc, getDocs, onSnapshot} from "firebase/firestore";
import db from "../../../firebase";
import styles from "./TaskManagerModal.module.css";
import {
    BucketTaskDraft,
    saveBucketSetup,
} from "../../../roomActions";
import IconClose from "assets/icon-close.svg";
import IconList from "assets/icon-list.svg";
import IconChevUp from "assets/icon-chevron-up.svg";
import IconChevDown from "assets/icon-chevron-down.svg";
import IconTrash from "assets/icon-trash.svg";
import { useTranslation } from "react-i18next";

type Props = {
    roomId: string;
    onClose: () => void;
};

type SetupStatus = "draft" | "ready" | "inProgress" | "finished";

type TaskRow = Required<BucketTaskDraft> & {
    iterationId?: string | null;
    iterationIndex?: number | null;
};

const tasksPerIterationOptions = [2, 3, 4];

const TaskManagerModal: React.FC<Props> = ({roomId, onClose}) => {
    const { t } = useTranslation();
    const [setupStatus, setSetupStatus] = useState<SetupStatus>("draft");
    const [tasks, setTasks] = useState<TaskRow[]>([]);
    const [tasksPerIter, setTasksPerIter] = useState<number>(4);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState<string | null>(null);

    // подтягиваем текущие tasks/состояние, если уже что-то есть
    useEffect(() => {
        const unsub = onSnapshot(doc(db, "rooms", roomId), (snap) => {
            if (!snap.exists()) return;
            const d = snap.data() as any;
            setSetupStatus((d.setupStatus as SetupStatus) ?? "draft");
            if (d.tasksPerIteration) setTasksPerIter(d.tasksPerIteration);
        });

        (async () => {
            const s = await getDocs(collection(db, "rooms", roomId, "tasks"));
            const rows = s.docs.map((d) => ({
                id: d.id,
                ...(d.data() as any),
            })) as TaskRow[];
            rows.sort((a, b) => {
                const ai = (a.iterationId ? parseInt(a.iterationId) : 0) * 1000 + (a.iterationIndex ?? 0);
                const bi = (b.iterationId ? parseInt(b.iterationId) : 0) * 1000 + (b.iterationIndex ?? 0);
                return ai - bi;
            });
            setTasks(rows);
        })();

        return () => unsub();
    }, [roomId]);

    // локальная группировка по итерациям для UI предпросмотра
    const groups = useMemo(() => {
        // если задач нет — группируем по текущему значению tasksPerIter
        const list = tasks.length ? tasks : [];
        const chunked: TaskRow[][] = [];
        for (let i = 0; i < list.length; i += tasksPerIter) {
            chunked.push(list.slice(i, i + tasksPerIter));
        }
        if (!list.length) chunked.push([]);
        return chunked;
    }, [tasks, tasksPerIter]);

    const addTask = () => {
        if (!title.trim()) return;
        setTasks((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                title: title.trim(),
                description: description.trim(),
                iterationId: null,
                iterationIndex: null,
            },
        ]);
        setTitle("");
        setDescription("");
    };

    const getGlobalIndex = (iterIdx: number, taskIdx: number) => {
        let global = 0;
        for (let i = 0; i < iterIdx; i++) global += groups[i].length;
        return global + taskIdx;
    };

    const moveOne = (iterIdx: number, taskIdx: number, dir: "up" | "down") => {
        const flat = groups.flat();
        const current = getGlobalIndex(iterIdx, taskIdx);
        const target = dir === "up" ? current - 1 : current + 1;

        if (target < 0 || target >= flat.length) return;

        const copy = [...flat];
        [copy[current], copy[target]] = [copy[target], copy[current]];
        setTasks(copy);
    };

    const removeAt = (iterIdx: number, taskIdx: number) => {
        const flat = groups.flat();
        const current = getGlobalIndex(iterIdx, taskIdx);
        const copy = [...flat];
        copy.splice(current, 1);
        setTasks(copy);
    };

    const handleSave = async () => {
        // Готовим плоский список без iterationId — серверная раскладка по N
        const drafts = tasks.map((t) => ({id: t.id, title: t.title, description: t.description}));
        await saveBucketSetup({roomId, tasks: drafts, tasksPerIteration: tasksPerIter});
        onClose();
    };
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalPanel}>
                <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                            fill="#1D1B20"/>
                    </svg>
                </button>
                <div className={styles.modalTitle}>{t("taskManagement")}</div>

                {/* Добавление задачи */}
                <div className={styles.form}>
                    <div className={styles.modalSubTitle}>{t("addTask")}</div>
                    <input
                        placeholder={t("taskNamePlaceholder")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <textarea
                        placeholder={t("taskDescriptionPlaceholder")}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                    />
                    <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={addTask}>
                        <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M4.41675 5.08268H0.916748V3.91602H4.41675V0.416016H5.58341V3.91602H9.08341V5.08268H5.58341V8.58268H4.41675V5.08268Z"
                                fill="#460B74"/>
                        </svg>
                        {t("addTaskToList")}
                    </button>
                </div>

                {/* Список задач + настройки итераций */}
                <div className={styles.toolbar}>
                    <div className={`${styles.modalSubTitle} ${styles.toolbarTitle}`}>{t("allTasks")} ({tasks.length})
                    </div>
                    <div className={styles.toolbarRight}>
                        <select
                            className={styles.btn}
                            value={tasksPerIter}
                            onChange={(e) => setTasksPerIter(parseInt(e.target.value, 10))}
                        >
                            {tasksPerIterationOptions.map((n) => (
                                <option key={n} value={n}>{t("taskPerIteration", { count: n })}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.iterations}>
                        {tasks.length > 0 && groups.map((g, i) => (
                            <div key={i} className={styles.iteration}>
                                <div className={styles.iterationTitle}>
                                    <svg width="16" height="12" viewBox="0 0 16 12" fill="none"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M5 1.5H14.75M5 6H14.75M5 10.5H14.75M1.25 1.5H1.2575M1.25 6H1.2575M1.25 10.5H1.2575"
                                            stroke="#A8A8A8" strokeWidth="1.5" strokeLinecap="round"
                                            strokeLinejoin="round"/>
                                    </svg>
                                    {t("iteration", {index: i + 1})}
                                </div>
                                <div className={styles.iterationList}>
                                    {g.map((task, idx) => (
                                        <div
                                            key={task.id}
                                            className={styles.taskRow}
                                            onMouseEnter={() => setHoveredTaskId(task.id)}
                                            onMouseLeave={() => setHoveredTaskId((prev) => (prev === task.id ? null : prev))}
                                        >
                                            <div className={styles.taskIndex}>{idx + 1}</div>
                                            <div className={styles.taskMain}>
                                                <div className={styles.taskTitle}>{task.title}</div>
                                                <button
                                                    className={`${isVisible == task.id ? styles.hideDescBtnActive : ''} ${styles.hideDescBtn}`}
                                                    onClick={() => setIsVisible((prev) => (prev === task.id ? null : task.id))}
                                                    aria-label={isVisible == task.id ? t("collapse") : t("expand")}
                                                >
                                                    {isVisible == task.id ? t("collapse") : t("expand")}
                                                </button>
                                                {isVisible == task.id && (
                                                    <div className={styles.taskDesc}>{task.description}</div>
                                                )}
                                            </div>
                                            <div className={styles.taskActions}>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => moveOne(i, idx, "up")}
                                                    title={t("moveUp")}
                                                    aria-label={t("moveUp")}
                                                >
                                                    <img src={IconChevUp} alt="↑"/>
                                                </button>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => moveOne(i, idx, "down")}
                                                    title={t("moveDown")}
                                                    aria-label={t("moveDown")}
                                                >
                                                    <img src={IconChevDown} alt="↓"/>
                                                </button>
                                                <button
                                                    className={styles.btn}
                                                    onClick={() => removeAt(i, idx)}
                                                    title={t("deleteTaskButton")}
                                                    aria-label={t("deleteTaskButton")}
                                                >
                                                    <img src={IconTrash} alt="🗑"/>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                <div className={styles.footer}>
                    <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave}>{t("saveChanges")}
                    </button>
                    <button className={`${styles.btn} ${styles.btnGhost}`} onClick={onClose}>{t("cancel")}</button>
                </div>
            </div>
        </div>
    );
};

export default TaskManagerModal;
